import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { describeFormula } from 'src/app/shared/formula/formula-plain';
import { INCOMPLETE_STEP_ERROR } from 'src/app/shared/formula/formula-engine.service';
import { formulaMessageText, formulaVariableLabel } from 'src/app/shared/formula/formula-messages';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil, timeout } from 'rxjs/operators';
import { ItemsService } from 'src/app/pages/items/services/items.service';
import { LoggerService } from 'src/app/services/logger.service';
import { FormulaContextService } from 'src/app/shared/formula/formula-context.service';
import { FormulaEngineService } from 'src/app/shared/formula/formula-engine.service';
import { FieldTypeRow,
  ComparisonOperator,
  CORE_FORMULA_VARIABLES,
  formulaFieldTypeId,
  isFormulaType,
  createDefaultFormula,
  createEmptyConditionalParts,
  FormulaAction,
  FormulaCondition,
  FormulaExecutionResult,
  FormulaJSON,
  FormulaStep,
  FormulaVariable,
  FormattingOperator,
  LogicMode,
  Operator,
  slugifyFieldKey,
  variableFromCustomField,
  normalizeRollupMode,
  normalizeQuoteImpact,
  derivedQuoteImpactFromFormula,
  quoteImpactFromAction,
  quoteImpactFromMetadata,
  targetFieldFromQuoteImpact,
  targetTypeFromQuoteImpact,
  RollupMode,
  QuoteImpact,
  clampDiscountPercent,
  DISCOUNT_PERCENT_TOKEN,
  isPercentOfUnitPriceAction,
  isReservedFieldName,
  RESERVED_NAMES_SHOWN,
  passesFieldNameRule,
  passesUnitSuffixRule,
  passesExecutionOrderRule,
} from 'src/app/shared/formula/formula.types';

export interface FormulaBuilderDialogData {
  name?: string;
  /**
   * The name of the field as already SAVED as a Formula field. Only this, unchanged, skips the name
   * rules (it may predate them). `name` alone can be prefilled from another form or type, so it never
   * does (card #553: "Qty" typed, then the type switched to Formula, skipped every rule).
   */
  savedName?: string;
  unit_of_measure?: string;
  formula_json?: FormulaJSON | string;
  field_key?: string;
  execution_order?: number;
  editMode?: boolean;
  existingNames?: string[];
  availableCustomFields?: any[];
  itemContext?: Record<string, number>;
}

@Component({
  selector: 'app-formula-builder-dialog',
  templateUrl: './formula-builder-dialog.component.html',
  styleUrls: ['./formula-builder-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    MatInputModule,
    TranslateModule,
  ],
})
export class FormulaBuilderDialogComponent implements OnInit, OnDestroy {
  headerForm: FormGroup;
  formula: FormulaJSON;
  variables: FormulaVariable[] = [...CORE_FORMULA_VARIABLES];
  testValues: Record<string, number> = {};
  result: FormulaExecutionResult = { result: 0 };
  saveAttempted = false;
  saveError = '';
  saving = false;
  variablesLoadWarning = '';
  operators = [
    { value: Operator.ADD, labelKey: 'COMMON.Formula op add' },
    { value: Operator.SUBTRACT, labelKey: 'COMMON.Formula op subtract' },
    { value: Operator.MULTIPLY, labelKey: 'COMMON.Formula op multiply' },
    { value: Operator.DIVIDE, labelKey: 'COMMON.Formula op divide' },
  ];
  comparisonOperators = [
    { value: ComparisonOperator.GREATER_THAN, labelKey: 'COMMON.is Greater Than' },
    { value: ComparisonOperator.LESS_THAN, labelKey: 'COMMON.is Less Than' },
    { value: ComparisonOperator.EQUAL_TO, labelKey: 'COMMON.is Equal To' },
    { value: ComparisonOperator.GREATER_THAN_OR_EQUAL, labelKey: 'COMMON.is Greater Than or Equal' },
    { value: ComparisonOperator.LESS_THAN_OR_EQUAL, labelKey: 'COMMON.is Less Than or Equal' },
  ];
  formattingOptions = [
    { value: FormattingOperator.NONE, labelKey: 'COMMON.No Rounding' },
    { value: FormattingOperator.CEILING, labelKey: 'COMMON.Round Up (Ceiling)' },
    { value: FormattingOperator.FLOOR, labelKey: 'COMMON.Round Down (Floor)' },
    { value: FormattingOperator.ROUND, labelKey: 'COMMON.Standard Rounding' },
  ];
  rollupModes: { value: RollupMode; labelKey: string }[] = [
    { value: 'none', labelKey: "COMMON.Don't combine" },
    { value: 'sum', labelKey: 'COMMON.Sum all products' },
    { value: 'weighted_average', labelKey: 'COMMON.Quantity-weighted average' },
  ];
  quoteImpacts: { value: QuoteImpact; labelKey: string }[] = [
    { value: 'none', labelKey: 'COMMON.No impact' },
    { value: 'unit_price', labelKey: 'COMMON.Set Unit Price' },
    { value: 'discount_percent', labelKey: 'COMMON.Set Discount %' },
  ];
  selectedFormatting: FormattingOperator = FormattingOperator.NONE;
  private customOperandStepIds = new Set<number>();
  private customConditionRight = false;
  private customTrueAction = false;
  private customFalseAction = false;
  private conditionValuePrompt: 'discount' | 'unit_price' | null = null;
  private trueActionPrompt: 'discount' | 'unit_price' | null = null;
  private falseActionPrompt: 'discount' | 'unit_price' | null = null;
  private itemSeed: Record<string, number> = {};
  private userEditedTestKeys = new Set<string>();
  /** Actions whose Quote impact the user picked in this dialog (their choice is never overridden). */
  private impactChosen = new Set<'true' | 'false'>();
  private _unsubscribeAll = new Subject<void>();
  private destroyed = false;

  constructor(
    public dialogRef: MatDialogRef<FormulaBuilderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FormulaBuilderDialogData,
    private itemsService: ItemsService,
    private engine: FormulaEngineService,
    private contextService: FormulaContextService,
    private translateService: TranslateService,
    private logger: LoggerService,
  ) {
    const incomingName = String(data?.name || '').trim();
    const incoming = this.parseFormula(data?.formula_json);
    const initial = incoming || createDefaultFormula(incomingName || this.defaultName(), data?.unit_of_measure || '');
    if (incomingName) {
      initial.metadata.field_label = incomingName;
      initial.metadata.field_key = data.field_key || slugifyFieldKey(incomingName);
    }
    if (data?.unit_of_measure) {
      initial.metadata.unit_of_measure = data.unit_of_measure;
    }
    if (!initial.logic_mode) {
      initial.logic_mode = 'recipe';
    }
    this.formula = initial;
    this.ensureModeDefaults(this.formula.logic_mode);
    if (this.formula.logic_mode === 'recipe') {
      this.extractFormattingFromSteps();
    }
    this.syncCustomFlagsFromFormula();
    // A brand-new formula opens with its first math step ready ("Start with" + "Then"), so the
    // user fills in a recipe instead of hunting for "Add Math Step" (Rod, 2026-10-09). Saved
    // formulas open exactly as stored. Inline rather than addOperation(): that recalculates,
    // and the header form does not exist yet.
    if (!incoming && this.formula.logic_mode === 'recipe' && this.steps.length === 1) {
      const nextId = Math.max(...this.steps.map((s) => s.step_id)) + 1;
      this.formula = {
        ...this.formula,
        steps: [...this.steps, { step_id: nextId, type: 'operation', operator: Operator.MULTIPLY, operand: null as any }],
      };
      this.customOperandStepIds.add(nextId);
    }
    this.mergeAvailableCustomFields(data?.availableCustomFields);
    this.seedTestValuesFromItem(data?.itemContext);

    this.headerForm = new FormGroup({
      name: new FormControl(initial.metadata.field_label, [Validators.required, Validators.maxLength(100)]),
      // Card #520: these were decoration (never checked before save) and type="number" hid junk.
      // A unit saved before the rule (e.g. "square meters") stays valid until it is changed.
      unit_of_measure: new FormControl(initial.metadata.unit_of_measure || '', [
        (c) => {
          const v = String(c.value ?? '').trim();
          return !v || v === String(initial.metadata.unit_of_measure || '').trim() || passesUnitSuffixRule(v)
            ? null
            : { unit: true };
        },
      ]),
      execution_order: new FormControl(String(data?.execution_order ?? 0), [
        Validators.required,
        // Card #566: the server's run-order rule (empty is left to `required`, as the pattern did).
        (c) => (c.value === '' || c.value == null || passesExecutionOrderRule(c.value) ? null : { pattern: true }),
      ]),
      rollup_mode: new FormControl(normalizeRollupMode(initial.metadata.rollup_mode)),
    });
    this.meta.rollup_mode = normalizeRollupMode(initial.metadata.rollup_mode);
    this.inheritQuoteImpactOntoActions();
    this.syncMetadataQuoteImpact();
    this.includeCurrentFormulaField();
  }

  ngOnInit(): void {
    this.runOrderOptions = this.buildRunOrderOptions();
    // The field being edited must not be one of its own variables (the API drops it by key).
    const excludeFieldKey = this.currentFieldKey();
    this.itemsService
      .getFormulaVariables(excludeFieldKey ? { excludeFieldKey } : {})
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
      next: (res: any) => {
        if (this.isFormulaVariableList(res?.data) && res.data.length) {
          this.variables = res.data;
        } else if (res?.data != null) {
          this.variablesLoadWarning = this.translateService.instant(
            'COMMON.Could not load custom variables',
          );
        }
        this.mergeAvailableCustomFields(this.data?.availableCustomFields);
        this.includeCurrentFormulaField();
        this.ensureSelectedVariablesVisible();
        this.applySeededTestValues();
        this.recalculate();
      },
      error: () => {
        this.variablesLoadWarning = this.translateService.instant(
          'COMMON.Could not load custom variables',
        );
        this.mergeAvailableCustomFields(this.data?.availableCustomFields);
        this.includeCurrentFormulaField();
        this.applySeededTestValues();
        this.recalculate();
      },
    });

    this.headerForm.get('name')?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((name) => {
      this.meta.field_label = name || '';
      if (!this.data?.field_key) {
        this.meta.field_key = slugifyFieldKey(name || 'formula');
        this.meta.target_field = targetFieldFromQuoteImpact(
          derivedQuoteImpactFromFormula(this.formula),
          this.meta.field_key,
        );
      }
      this.includeCurrentFormulaField();
    });

    this.headerForm.get('unit_of_measure')?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((unit) => {
      this.meta.unit_of_measure = unit || '';
      this.recalculate();
    });

    this.headerForm.get('rollup_mode')?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((mode) => {
      this.meta.rollup_mode = normalizeRollupMode(mode);
    });

    this.recalculate();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private isFormulaVariableList(value: unknown): value is FormulaVariable[] {
    return Array.isArray(value) && value.every((variable) => {
      if (!variable || typeof variable !== 'object') {
        return false;
      }
      const candidate = variable as Partial<FormulaVariable>;
      return (
        typeof candidate.id === 'string' &&
        candidate.id.length > 0 &&
        typeof candidate.label === 'string' &&
        candidate.label.length > 0 &&
        (candidate.type === 'Number' ||
          candidate.type === 'Currency' ||
          candidate.type === 'Percent')
      );
    });
  }

  get isRecipeMode(): boolean {
    return this.formula.logic_mode !== 'conditional';
  }

  get isConditionalMode(): boolean {
    return this.formula.logic_mode === 'conditional';
  }

  get steps(): FormulaStep[] {
    return this.formula.steps || [];
  }

  get condition(): FormulaCondition {
    return (
      this.formula.condition || {
        left: '',
        operator: ComparisonOperator.GREATER_THAN,
        right: '',
      }
    );
  }

  get trueAction(): FormulaAction {
    return this.formula.true_action || { type: 'assignment', value: '' };
  }

  get falseAction(): FormulaAction {
    return this.formula.false_action || { type: 'assignment', value: '' };
  }

  get hasConditionalContent(): boolean {
    return !this.isBlank(this.condition.left)
      || !this.isBlank(this.condition.right)
      || !this.isBlank(this.trueAction.value)
      || !this.isBlank(this.falseAction.value);
  }

  get usedVariables(): string[] {
    const ids = this.contextService.getUsedVariableIds(this.buildExecutableFormula());
    const discountSource = this.singleNumericDiscountAction();
    if (discountSource && !ids.includes(DISCOUNT_PERCENT_TOKEN)) {
      return [...ids, DISCOUNT_PERCENT_TOKEN];
    }
    return ids;
  }

  parseFormula(raw: FormulaJSON | string | undefined): FormulaJSON | null {
    if (!raw) return null;
    let parsed: any = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return null;
      }
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return this.normalizeFormula(parsed);
  }

  /**
   * metadata, logic_mode and steps always present. A recipe stored without metadata (a shape the
   * per-item recipes and old saves use) used to throw a TypeError and the builder never opened.
   */
  private normalizeFormula(formula: any): FormulaJSON {
    const label = String(this.data?.name || formula?.metadata?.field_label || '').trim() || this.defaultName();
    const defaults = createDefaultFormula(label, this.data?.unit_of_measure || '');
    const metadata = formula?.metadata && typeof formula.metadata === 'object' ? formula.metadata : {};
    const conditional = formula?.logic_mode === 'conditional';
    return {
      ...formula,
      metadata: { ...defaults.metadata, ...metadata },
      logic_mode: conditional ? 'conditional' : 'recipe',
      steps: Array.isArray(formula?.steps) ? formula.steps : conditional ? [] : defaults.steps,
    };
  }

  /** The formula's metadata, created if a code path ever replaced the formula without one. */
  private get meta(): FormulaJSON['metadata'] {
    if (!this.formula.metadata || typeof this.formula.metadata !== 'object') {
      this.formula = { ...this.formula, metadata: { ...createDefaultFormula().metadata } };
    }
    return this.formula.metadata;
  }

  setLogicMode(mode: LogicMode): void {
    if (this.formula.logic_mode === mode) return;
    this.impactChosen?.clear(); // new actions: no Quote impact chosen for them yet
    this.formula = { ...this.formula, logic_mode: mode };
    this.ensureModeDefaults(mode);
    this.syncCustomFlagsFromFormula();
    this.saveAttempted = false;
    this.saveError = '';
    this.recalculate();
  }

  updateStep(index: number, updates: Partial<FormulaStep>): void {
    const steps = [...this.steps];
    steps[index] = { ...steps[index], ...updates };
    this.formula = { ...this.formula, steps };
    this.recalculate();
  }

  onOperandSourceChange(index: number, value: string): void {
    const stepId = this.steps[index]?.step_id;
    if (value === '__custom__') {
      if (stepId !== undefined) {
        this.customOperandStepIds.add(stepId);
      }
      this.updateStep(index, { operand: null as any });
    } else {
      if (stepId !== undefined) {
        this.customOperandStepIds.delete(stepId);
      }
      this.updateStep(index, { operand: value });
    }
  }

  isCustomOperand(step: FormulaStep): boolean {
    return this.customOperandStepIds.has(step.step_id) || typeof step.operand === 'number';
  }

  updateCustomOperand(index: number, raw: string): void {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
      this.updateStep(index, { operand: null as any });
      return;
    }
    const parsed = Number(trimmed);
    this.updateStep(index, { operand: Number.isFinite(parsed) ? parsed : null as any });
  }

  addOperation(): void {
    const nextId = this.steps.length ? Math.max(...this.steps.map((s) => s.step_id)) + 1 : 1;
    this.formula = {
      ...this.formula,
      steps: [
        ...this.steps,
        { step_id: nextId, type: 'operation', operator: Operator.MULTIPLY, operand: null as any },
      ],
    };
    this.customOperandStepIds.add(nextId);
    this.recalculate();
  }

  onFormattingChange(value: FormattingOperator): void {
    this.selectedFormatting = value || FormattingOperator.NONE;
    this.recalculate();
  }

  removeStep(index: number): void {
    if (this.steps[index]?.type === 'source') return;
    const removedStep = this.steps[index];
    if (removedStep?.step_id !== undefined) {
      this.customOperandStepIds.delete(removedStep.step_id);
    }
    this.formula = {
      ...this.formula,
      steps: this.steps.filter((_, i) => i !== index),
    };
    this.recalculate();
  }

  updateCondition(updates: Partial<FormulaCondition>): void {
    this.formula = {
      ...this.formula,
      condition: { ...this.condition, ...updates },
    };
    this.applySeededTestValues();
    this.recalculate();
  }

  onConditionLeftChange(value: string): void {
    const prompt = this.valuePromptFor(value);
    const updates: Partial<FormulaCondition> = { left: value };
    if (prompt) {
      this.conditionValuePrompt = prompt;
      this.customConditionRight = true;
      if (typeof this.condition.right === 'string') {
        updates.right = null as any;
      }
    } else if (this.valuePromptFor(this.condition.left)) {
      this.conditionValuePrompt = this.isCustomConditionRight() ? this.conditionValuePrompt : null;
    }
    this.updateCondition(updates);
  }

  onConditionRightSourceChange(value: string): void {
    const prompt = this.valuePromptFor(value);
    if (prompt) {
      this.conditionValuePrompt = prompt;
      this.customConditionRight = true;
      this.updateCondition({ right: null as any });
      return;
    }
    if (value === '__custom__') {
      this.conditionValuePrompt = this.valuePromptFor(this.condition.left);
      this.customConditionRight = true;
      this.updateCondition({ right: 0 });
      return;
    }
    this.conditionValuePrompt = null;
    this.customConditionRight = false;
    this.updateCondition({ right: value });
  }

  updateCustomConditionRight(raw: string): void {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
      this.updateCondition({ right: null as any });
      return;
    }
    const parsed = Number(trimmed);
    // A percentage outside 0-100 is kept as typed and flagged (conditionPercentOutOfRange), never
    // clamped silently: clamping left "1000000" on screen while 100 was stored (Rod, 2026-10-09).
    this.updateCondition({ right: Number.isFinite(parsed) ? parsed : (null as any) });
  }

  isCustomConditionRight(): boolean {
    return this.customConditionRight || typeof this.condition.right === 'number';
  }

  hideConditionRightFieldDropdown(): boolean {
    if (!this.valuePromptFor(this.condition.left)) {
      return false;
    }
    return this.isCustomConditionRight() || this.isBlank(this.condition.right);
  }

  conditionRightSelectValue(): string {
    if (this.isCustomConditionRight()) {
      if (!this.hideConditionRightFieldDropdown()) {
        if (this.conditionValuePrompt === 'discount') {
          return 'Item.Discount_Percent';
        }
        if (this.conditionValuePrompt === 'unit_price') {
          return 'Item.Unit_Price';
        }
      }
      return '__custom__';
    }
    return String(this.condition.right ?? '');
  }

  conditionValueLabelKey(): string | null {
    const prompt = this.conditionValuePrompt || this.valuePromptFor(this.condition.left);
    if (prompt === 'discount') {
      return 'COMMON.Discount value';
    }
    if (prompt === 'unit_price') {
      return 'COMMON.Unit price value';
    }
    return null;
  }

  conditionValuePlaceholder(): string {
    const prompt = this.conditionValuePrompt || this.valuePromptFor(this.condition.left);
    if (prompt === 'discount') {
      return this.translateService.instant('COMMON.Enter discount');
    }
    if (prompt === 'unit_price') {
      return this.translateService.instant('COMMON.Enter unit price');
    }
    return '0';
  }

  onActionSourceChange(which: 'true' | 'false', value: string): void {
    const prompt = this.valuePromptFor(value);
    if (prompt) {
      if (which === 'true') {
        this.trueActionPrompt = prompt;
        this.customTrueAction = true;
      } else {
        this.falseActionPrompt = prompt;
        this.customFalseAction = true;
      }
      // Choosing "Discount %" / "Unit price" as the result means the quote line should get it: the
      // impact used to stay "No impact" unless the separate Quote impact dropdown was also changed,
      // so the panel showed the value and the line never got it (card 27 bug 3). A choice the user
      // made in that dropdown is kept.
      this.updateAction(which, null as any, !this.impactChosen?.has(which));
      return;
    }
    if (value === '__custom__') {
      if (which === 'true') {
        this.trueActionPrompt = null;
        this.customTrueAction = true;
      } else {
        this.falseActionPrompt = null;
        this.customFalseAction = true;
      }
      this.updateAction(which, null as any);
      return;
    }
    if (which === 'true') {
      this.trueActionPrompt = null;
      this.customTrueAction = false;
    } else {
      this.falseActionPrompt = null;
      this.customFalseAction = false;
    }
    this.updateAction(which, value);
  }

  updateCustomAction(which: 'true' | 'false', raw: string): void {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
      this.updateAction(which, null as any);
      return;
    }
    const parsed = Number(trimmed);
    // Kept as typed; percentOutOfRange shows the error and validation blocks the save.
    this.updateAction(which, Number.isFinite(parsed) ? parsed : (null as any));
  }

  /**
   * Card #522: say WHICH name is taken and list the ones a person would type, instead of three
   * examples that made "Qty" being refused look arbitrary.
   */
  reservedNameMessage(name: string): string {
    return this.translateService.instant('COMMON.Formula field name reserved named', {
      name: String(name || '').trim(),
      list: RESERVED_NAMES_SHOWN.join(', '),
    });
  }

  /** True when `name` is the name this field was already saved with as a Formula field. */
  private isUnchangedSavedName(name: string): boolean {
    const saved = String(this.data?.savedName || '').trim();
    return !!saved && name === saved;
  }

  /** The inline message under Field Name as the user types (reserved / characters), or ''. */
  get fieldNameHint(): string {
    const name = String(this.headerForm?.get('name')?.value || '').trim();
    // A saved Formula field keeps its name unchecked until it is changed (it may predate the rules).
    if (!name || this.isUnchangedSavedName(name)) {
      return '';
    }
    if (name.length > 50) {
      return this.translateService.instant('COMMON.Field name max 50');
    }
    if (isReservedFieldName(name)) {
      return this.reservedNameMessage(name);
    }
    if (!passesFieldNameRule(name)) {
      return this.translateService.instant('COMMON.Field name characters');
    }
    return '';
  }

  /** The help line under "What the result changes", for the option picked (Rod, 2026-10-09). */
  quoteImpactHintKey(impact: string | null | undefined): string {
    if (impact === 'unit_price') {
      return 'COMMON.Quote impact hint unit price';
    }
    if (impact === 'discount_percent') {
      return 'COMMON.Quote impact hint discount';
    }
    return 'COMMON.Quote impact hint none';
  }

  /** A typed percentage (Discount % / percent of unit price) outside 0-100. */
  percentOutOfRange(which: 'true' | 'false'): boolean {
    const action = which === 'true' ? this.trueAction : this.falseAction;
    return this.isDiscountAction(which) && typeof action.value === 'number' && (action.value < 0 || action.value > 100);
  }

  /** The condition compares a Discount % against a typed value outside 0-100. */
  get conditionPercentOutOfRange(): boolean {
    const right = this.condition?.right;
    return this.isDiscountConditionInput && typeof right === 'number' && (right < 0 || right > 100);
  }

  isCustomAction(which: 'true' | 'false'): boolean {
    const action = which === 'true' ? this.trueAction : this.falseAction;
    const flagged = which === 'true' ? this.customTrueAction : this.customFalseAction;
    return flagged || typeof action.value === 'number';
  }

  actionSelectValue(which: 'true' | 'false'): string {
    const prompt = which === 'true' ? this.trueActionPrompt : this.falseActionPrompt;
    if (this.isCustomAction(which)) {
      if (prompt === 'discount') {
        return 'Item.Discount_Percent';
      }
      if (prompt === 'unit_price') {
        return 'Item.Unit_Price';
      }
      return '__custom__';
    }
    const action = which === 'true' ? this.trueAction : this.falseAction;
    return String(action.value ?? '');
  }

  actionValueLabelKey(which: 'true' | 'false'): string | null {
    const prompt = which === 'true' ? this.trueActionPrompt : this.falseActionPrompt;
    if (prompt === 'discount') {
      return 'COMMON.Discount value';
    }
    if (prompt === 'unit_price') {
      return 'COMMON.Unit price value';
    }
    return null;
  }

  actionValuePlaceholder(which: 'true' | 'false'): string {
    const prompt = this.actionPrompt(which);
    if (prompt === 'discount') {
      return this.translateService.instant('COMMON.Enter discount');
    }
    if (prompt === 'unit_price') {
      return this.translateService.instant('COMMON.Enter unit price');
    }
    return '0';
  }

  isDiscountAction(which: 'true' | 'false'): boolean {
    const action = which === 'true' ? this.trueAction : this.falseAction;
    return this.actionPrompt(which) === 'discount' || isPercentOfUnitPriceAction(action);
  }

  get isDiscountConditionInput(): boolean {
    return this.conditionValuePrompt === 'discount';
  }

  private actionPrompt(which: 'true' | 'false'): 'discount' | 'unit_price' | null {
    return which === 'true' ? this.trueActionPrompt : this.falseActionPrompt;
  }

  updateAction(which: 'true' | 'false', value: string | number, impactFollowsPrompt = false): void {
    const prompt = this.actionPrompt(which);
    const previous = which === 'true' ? this.trueAction : this.falseAction;
    const action: FormulaAction = { type: 'assignment', value };
    if (prompt === 'discount') {
      action.value_mode = 'percent_of_unit_price';
    }
    const promptImpact: QuoteImpact | undefined =
      prompt === 'discount' ? 'discount_percent' : prompt === 'unit_price' ? 'unit_price' : undefined;
    action.quote_impact = this.sanitizeActionImpact(
      prompt,
      impactFollowsPrompt && promptImpact ? promptImpact : previous?.quote_impact,
    );
    if (which === 'true') {
      this.formula = { ...this.formula, true_action: action };
    } else {
      this.formula = { ...this.formula, false_action: action };
    }
    this.syncMetadataQuoteImpact();
    this.applySeededTestValues();
    this.syncFormulaDiscountToTestBench();
    this.recalculate();
  }

  showQuoteImpact(which: 'true' | 'false'): boolean {
    const prompt = this.actionPrompt(which);
    return prompt === 'discount' || prompt === 'unit_price';
  }

  quoteImpactsFor(which: 'true' | 'false'): { value: QuoteImpact; labelKey: string }[] {
    const prompt = this.actionPrompt(which);
    if (prompt === 'discount') {
      return this.quoteImpacts.filter(
        (impact) => impact.value === 'none' || impact.value === 'discount_percent',
      );
    }
    if (prompt === 'unit_price') {
      return this.quoteImpacts.filter(
        (impact) => impact.value === 'none' || impact.value === 'unit_price',
      );
    }
    return this.quoteImpacts.filter((impact) => impact.value === 'none');
  }

  actionQuoteImpact(which: 'true' | 'false'): QuoteImpact {
    const action = which === 'true' ? this.trueAction : this.falseAction;
    return this.sanitizeActionImpact(this.actionPrompt(which), action?.quote_impact);
  }

  updateActionQuoteImpact(which: 'true' | 'false', impact: QuoteImpact): void {
    (this.impactChosen ??= new Set<'true' | 'false'>()).add(which);
    const action = which === 'true' ? this.trueAction : this.falseAction;
    const next: FormulaAction = {
      ...action,
      quote_impact: this.sanitizeActionImpact(this.actionPrompt(which), impact),
    };
    if (which === 'true') {
      this.formula = { ...this.formula, true_action: next };
    } else {
      this.formula = { ...this.formula, false_action: next };
    }
    this.syncMetadataQuoteImpact();
    this.recalculate();
  }

  private sanitizeActionImpact(
    prompt: 'discount' | 'unit_price' | null,
    current?: QuoteImpact | string,
  ): QuoteImpact {
    const impact = normalizeQuoteImpact(current);
    if (prompt === 'discount' && impact === 'discount_percent') {
      return 'discount_percent';
    }
    if (prompt === 'unit_price' && impact === 'unit_price') {
      return 'unit_price';
    }
    return 'none';
  }

  private inheritQuoteImpactOntoActions(): void {
    const meta = quoteImpactFromMetadata(this.meta);
    if (meta === 'none') {
      return;
    }
    if (this.trueAction && quoteImpactFromAction(this.trueAction) === undefined) {
      const inherited = this.sanitizeActionImpact(this.trueActionPrompt, meta);
      if (inherited !== 'none') {
        this.formula = {
          ...this.formula,
          true_action: { ...this.trueAction, quote_impact: inherited },
        };
      }
    }
    if (this.falseAction && quoteImpactFromAction(this.falseAction) === undefined) {
      const inherited = this.sanitizeActionImpact(this.falseActionPrompt, meta);
      if (inherited !== 'none') {
        this.formula = {
          ...this.formula,
          false_action: { ...this.falseAction, quote_impact: inherited },
        };
      }
    }
  }

  private syncMetadataQuoteImpact(): void {
    const quoteImpact = derivedQuoteImpactFromFormula(this.formula);
    const metadata = this.meta;
    metadata.quote_impact = quoteImpact;
    metadata.target_type = targetTypeFromQuoteImpact(quoteImpact);
    metadata.target_field = targetFieldFromQuoteImpact(quoteImpact, metadata.field_key);
  }

  resetConditional(): void {
    this.impactChosen?.clear(); // new actions: no Quote impact chosen for them yet
    const empty = createEmptyConditionalParts();
    this.formula = {
      ...this.formula,
      logic_mode: 'conditional',
      condition: empty.condition,
      true_action: empty.true_action,
      false_action: empty.false_action,
    };
    this.customConditionRight = false;
    this.customTrueAction = false;
    this.customFalseAction = false;
    this.conditionValuePrompt = null;
    this.trueActionPrompt = null;
    this.falseActionPrompt = null;
    this.saveAttempted = false;
    this.saveError = '';
    this.recalculate();
  }

  testValueDisplay(varId: string): string | number {
    const value = this.testValues[varId];
    return value === undefined || value === null ? '' : value;
  }

  onTestValueChange(key: string, value: string): void {
    const trimmed = String(value ?? '').trim();
    const next = { ...this.testValues };
    if (!trimmed) {
      delete next[key];
    } else {
      const parsed = parseFloat(trimmed);
      if (Number.isFinite(parsed)) {
        next[key] = key === DISCOUNT_PERCENT_TOKEN ? clampDiscountPercent(parsed) : parsed;
      } else {
        delete next[key];
      }
    }
    this.userEditedTestKeys.add(key);
    this.testValues = next;
    if (key === DISCOUNT_PERCENT_TOKEN && Number.isFinite(next[key])) {
      this.syncTestBenchDiscountToFormula(next[key]);
    }
    this.recalculate();
  }

  testInputMax(varId: string): number | null {
    return varId === DISCOUNT_PERCENT_TOKEN ? 100 : null;
  }

  testInputMin(varId: string): number | null {
    return varId === DISCOUNT_PERCENT_TOKEN ? 0 : null;
  }

  recalculate(): void {
    if (this.isConditionalMode && !this.hasConditionalContent) {
      this.result = { result: 0 };
      return;
    }
    this.result = this.engine.execute(this.buildExecutableFormula(), this.testBenchContext());
  }

  /**
   * The test bench's inputs: every variable the formula uses, 0 unless the user typed a value. The
   * engine now refuses an unresolved variable (a quote line must not price from a guessed 0), so the
   * bench supplies its own explicit zeros. Quote lines never get these.
   */
  private testBenchContext(): Record<string, number> {
    const context: Record<string, number> = {};
    for (const id of this.usedVariables || []) {
      context[id] = 0;
    }
    return { ...context, ...(this.testValues || {}) };
  }

  /** A variable's name as shown: core values (Quantity, Unit price…) in the reader's language. */
  variableLabel(id: string): string {
    return formulaVariableLabel(id, this.variables, (key, params) => this.translateService.instant(key, params));
  }

  /**
   * An engine / server message as shown to the user. The engine's own strings are compared in
   * code (INCOMPLETE_STEP_ERROR, 'Unknown variable X') and match the backend, so they are
   * translated here, at display, never at the source.
   */
  messageText(message: unknown): string {
    return formulaMessageText(
      message,
      (key, params) => this.translateService.instant(key, params),
      (id) => this.variableLabel(id),
    );
  }

  /** The name a brand-new calculation starts with, in the reader's language. */
  private defaultName(): string {
    const key = 'COMMON.New calculation default name';
    const name = this.translateService?.instant?.(key);
    return typeof name === 'string' && name && name !== key ? name : 'New calculation';
  }

  /**
   * A step still waiting for its value (a new formula opens with an empty Step 2): not an error
   * yet, so the result card stays calm instead of turning red the moment the dialog opens.
   * Saving still refuses it (validateRecipeForSave).
   */
  get resultIncomplete(): boolean {
    return this.result?.error === INCOMPLETE_STEP_ERROR;
  }

  /**
   * Numbers typed straight into the formula, shown read-only under the Test Bench inputs (Rod,
   * 2026-10-11, option A): "Quantity × Weight × 10" listed only Quantity and Weight, which read as
   * if step 3 were skipped. Each row names where the number sits ("Step 3", "When", "If yes").
   */
  get fixedNumbers(): { label: string; value: string }[] {
    const isNum = (v: unknown) =>
      typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)));
    const fmt = (v: unknown) => String(Math.round(Number(v) * 10000) / 10000);
    const t = (key: string, params?: any) => this.translateService.instant(key, params);
    const out: { label: string; value: string }[] = [];
    if (this.isRecipeMode) {
      this.steps.forEach((step, idx) => {
        const v = step.type === 'source' ? step.value : step.type === 'operation' ? step.operand : undefined;
        if (isNum(v)) {
          out.push({ label: t('COMMON.Formula step', { n: idx + 1 }), value: fmt(v) });
        }
      });
      return out;
    }
    if (isNum(this.condition.right)) {
      out.push({ label: t('COMMON.The Condition'), value: fmt(this.condition.right) });
    }
    const action = (which: 'true' | 'false', key: string) => {
      const v = (which === 'true' ? this.trueAction : this.falseAction).value;
      if (isNum(v)) {
        out.push({ label: t(key), value: `${fmt(v)}${this.isDiscountAction(which) ? ' %' : ''}` });
      }
    };
    action('true', 'COMMON.If True (THEN)');
    action('false', 'COMMON.If False (ELSE)');
    return out;
  }

  /**
   * The formula read out in plain words under the Test Bench result (Rod, 2026-10-09):
   *   words   — "Weight × 10 = Shipping Cost"
   *   numbers — "10 × 10 = 100 USD"
   * Steps apply left to right (the engine has no operator precedence), so an earlier + or − is
   * bracketed before a × or ÷, exactly as the engine computes it. The result is the engine's own.
   */
  get plainFormula(): { words: string; numbers: string } | null {
    const t = (key: string) => this.translateService.instant(key);
    const unit = String(this.headerForm?.value?.unit_of_measure || '').trim();
    const ctx = this.testBenchContext();
    const n = Number(this.result?.result);
    return describeFormula(
      this.isRecipeMode ? { ...this.formula, steps: this.steps } : this.formula,
      {
        name: String(this.headerForm?.value?.name || '').trim() || t('COMMON.Calculation name'),
        label: (id) => this.variableLabel(id),
        value: (id) => Number(ctx[id] ?? 0),
        resultText: this.resultIncomplete
          ? '?'
          : this.result?.error
          ? this.messageText(this.result.error)
          : `${Number.isFinite(n) ? String(Math.round(n * 10000) / 10000) : '?'}${unit ? ' ' + unit : ''}`,
        t,
        formatting: this.isRecipeMode ? String(this.selectedFormatting) : undefined,
        isPercentAction: (which) => this.isDiscountAction(which),
      },
    );
  }


  cancel(): void {
    this.dialogRef.close(null);
  }

  async save(): Promise<void> {
    this.saveAttempted = true;
    this.saveError = this.validateForSave();
    if (this.saveError) {
      this.headerForm.markAllAsTouched();
      return;
    }
    if (this.saving) {
      return;
    }
    this.saving = true;

    const name = String(this.headerForm.value.name || '').trim();
    const unit = this.headerForm.value.unit_of_measure || '';
    // The existing key is sent back unchanged on a rename: the server moves it to the new name
    // when no formula uses the field (card #519) and keeps it when one does, so the rename never
    // breaks a formula.
    const fieldKey = this.data?.field_key || slugifyFieldKey(name);
    const executable = this.isConditionalMode && !this.hasConditionalContent
      ? this.buildRecipeAfterConditionalReset(name, unit)
      : this.buildExecutableFormula();
    const quoteImpact = derivedQuoteImpactFromFormula(executable);
    const formula: FormulaJSON = {
      ...executable,
      // Step labels are dropped: nothing reads them, and they did not follow the chosen values.
      ...(Array.isArray(executable.steps) ? { steps: executable.steps.map(({ label, ...step }) => step) } : {}),
      metadata: {
        ...this.formula.metadata,
        field_label: name,
        field_key: fieldKey,
        unit_of_measure: unit,
        quote_impact: quoteImpact,
        target_type: targetTypeFromQuoteImpact(quoteImpact),
        target_field: targetFieldFromQuoteImpact(quoteImpact, fieldKey),
        rollup_mode: normalizeRollupMode(this.headerForm.value.rollup_mode),
      },
    };

    try {
      const res: any = await firstValueFrom(
        this.itemsService.validateFormula({
          formula_json: formula,
          context: this.testBenchContext(),
        }).pipe(takeUntil(this._unsubscribeAll), timeout(10000)),
      );
      if (this.destroyed) {
        this.saving = false;
        this.closeFormulaDialog();
        return;
      }
      if (res?.data?.error || res?.status === 0) {
        this.saveError =
          this.messageText(String(res?.data?.error || res?.message || '')) ||
          this.translateService.instant('COMMON.Formula server validation failed');
        this.saving = false;
        return;
      }
    } catch (err: any) {
      if (this.destroyed || err?.name === 'EmptyError') {
        this.saving = false;
        this.closeFormulaDialog();
        return;
      }
      this.logger.log('formula:validateFormula network error', err?.message || 'failed');
      const isTimeout = err?.name === 'TimeoutError' || /timeout/i.test(String(err?.message || ''));
      this.saveError = this.translateService.instant(
        isTimeout ? 'COMMON.Formula validation timed out' : 'COMMON.Formula server validation failed',
      );
      this.saving = false;
      return;
    }

    try {
      this.dialogRef.close({
        field_type_id: formulaFieldTypeId(),
        name,
        unit_of_measure: unit,
        field_key: fieldKey,
        execution_order: Number(this.headerForm.value.execution_order) || 0,
        formula_json: formula,
        customer_custom_field_values: [],
      });
    } catch (err) {
      this.logger.log('formula:save dialog close error', err?.message || 'skipped');
    } finally {
      this.saving = false;
    }
  }

  private closeFormulaDialog(): void {
    try {
      this.dialogRef.close(null);
    } catch (err: any) {
      this.logger.log('formula:save dialog close error', err?.message || 'skipped');
    }
  }

  private validateForSave(): string {
    const name = String(this.headerForm.get('name')?.value || '').trim();
    if (!name) {
      return this.translateService.instant('COMMON.Calculation name required');
    }
    if (name.length > 100) {
      return this.translateService.instant('COMMON.Field name max 50');
    }
    const unchanged = this.isUnchangedSavedName(name);
    if (!unchanged && name.length > 50) {
      return this.translateService.instant('COMMON.Field name max 50');
    }
    if (!unchanged && isReservedFieldName(name)) {
      return this.reservedNameMessage(name);
    }
    if (!unchanged && !passesFieldNameRule(name)) {
      return this.translateService.instant('COMMON.Field name characters');
    }

    const existing = (this.data?.existingNames || [])
      .map((n) => String(n || '').trim().toLowerCase())
      .filter(Boolean);
    const original = String(this.data?.name || '').trim().toLowerCase();
    if (existing.includes(name.toLowerCase()) && name.toLowerCase() !== original) {
      return this.translateService.instant('COMMON.Formula field name exists');
    }

    if (this.headerForm.get('unit_of_measure')?.invalid) {
      return this.translateService.instant('COMMON.Unit suffix invalid');
    }
    if (this.headerForm.get('execution_order')?.invalid) {
      return this.translateService.instant('COMMON.Execution order whole number');
    }

    if (this.isConditionalMode) {
      if (!this.hasConditionalContent) {
        return '';
      }
      return this.validateConditionalForSave();
    }
    return this.validateRecipeForSave();
  }

  private validateRecipeForSave(): string {
    const source = this.steps.find((s) => s.type === 'source');
    if (!source?.value) {
      return this.translateService.instant('COMMON.Formula source required');
    }

    for (const step of this.steps) {
      if (step.type === 'operation') {
        if (!step.operator) {
          return this.translateService.instant('COMMON.Formula operator required');
        }
        if (typeof step.operand === 'string' && !step.operand.trim()) {
          return this.translateService.instant('COMMON.Formula operand required');
        }
        if (step.operand === undefined || step.operand === null || step.operand === '') {
          return this.translateService.instant('COMMON.Formula operand required');
        }
        if (typeof step.operand === 'number' && !Number.isFinite(step.operand)) {
          return this.translateService.instant('COMMON.Formula enter a number');
        }
        if (step.operator === Operator.DIVIDE && typeof step.operand === 'number' && step.operand === 0) {
          return this.translateService.instant('COMMON.Cannot divide by zero');
        }
      }
    }

    this.recalculate();
    if (this.result.error) {
      return this.messageText(this.result.error);
    }

    return '';
  }

  private validateConditionalForSave(): string {
    if (!this.condition.left) {
      return this.translateService.instant('COMMON.Conditional left required');
    }
    if (!this.condition.operator) {
      return this.translateService.instant('COMMON.Conditional operator required');
    }
    if (
      this.condition.right === undefined ||
      this.condition.right === null ||
      this.condition.right === ''
    ) {
      return this.translateService.instant('COMMON.Conditional right required');
    }
    if (typeof this.condition.right === 'number' && !Number.isFinite(this.condition.right)) {
      return this.translateService.instant('COMMON.Formula enter a number');
    }
    if (this.conditionPercentOutOfRange) {
      return this.translateService.instant('COMMON.Enter 0 to 100');
    }
    if (
      this.trueAction.value === undefined ||
      this.trueAction.value === null ||
      this.trueAction.value === ''
    ) {
      return this.translateService.instant('COMMON.Conditional then required');
    }
    if (typeof this.trueAction.value === 'number' && !Number.isFinite(this.trueAction.value)) {
      return this.translateService.instant('COMMON.Formula enter a number');
    }
    if (this.isDiscountAction('true') && typeof this.trueAction.value === 'number') {
      if (this.trueAction.value < 0 || this.trueAction.value > 100) {
        return this.translateService.instant('COMMON.Enter 0 to 100');
      }
    }
    if (
      this.falseAction.value === undefined ||
      this.falseAction.value === null ||
      this.falseAction.value === ''
    ) {
      return this.translateService.instant('COMMON.Conditional else required');
    }
    if (typeof this.falseAction.value === 'number' && !Number.isFinite(this.falseAction.value)) {
      return this.translateService.instant('COMMON.Formula enter a number');
    }
    if (this.isDiscountAction('false') && typeof this.falseAction.value === 'number') {
      if (this.falseAction.value < 0 || this.falseAction.value > 100) {
        return this.translateService.instant('COMMON.Enter 0 to 100');
      }
    }

    this.recalculate();
    if (this.result.error) {
      return this.messageText(this.result.error);
    }

    return '';
  }

  private ensureModeDefaults(mode: LogicMode): void {
    if (mode === 'conditional') {
      const hasCondition =
        !!this.formula.condition?.left ||
        (this.formula.condition?.right !== undefined &&
          this.formula.condition?.right !== null &&
          this.formula.condition?.right !== '') ||
        (this.formula.true_action?.value !== undefined &&
          this.formula.true_action?.value !== null &&
          this.formula.true_action?.value !== '') ||
        (this.formula.false_action?.value !== undefined &&
          this.formula.false_action?.value !== null &&
          this.formula.false_action?.value !== '');

      if (!hasCondition) {
        const empty = createEmptyConditionalParts();
        this.formula = {
          ...this.formula,
          logic_mode: 'conditional',
          condition: empty.condition,
          true_action: empty.true_action,
          false_action: empty.false_action,
        };
      }
      return;
    }

    if (!this.formula.steps?.length) {
      const defaults = createDefaultFormula(
        this.formula.metadata?.field_label || this.defaultName(),
        this.formula.metadata?.unit_of_measure || '',
      );
      this.formula = {
        ...this.formula,
        logic_mode: 'recipe',
        steps: defaults.steps,
      };
    }
  }

  private syncCustomFlagsFromFormula(): void {
    this.customConditionRight = typeof this.formula.condition?.right === 'number';
    this.customTrueAction = typeof this.formula.true_action?.value === 'number';
    this.customFalseAction = typeof this.formula.false_action?.value === 'number';
    this.conditionValuePrompt =
      this.valuePromptFor(this.formula.condition?.left) ||
      (this.customConditionRight ? this.conditionValuePrompt : null);
    this.trueActionPrompt = this.promptFromAction(this.formula.true_action, this.customTrueAction, this.trueActionPrompt);
    this.falseActionPrompt = this.promptFromAction(this.formula.false_action, this.customFalseAction, this.falseActionPrompt);
    this.customOperandStepIds.clear();
    (this.formula.steps || []).forEach((step) => {
      if (step.type === 'operation' && typeof step.operand === 'number') {
        this.customOperandStepIds.add(step.step_id);
      }
    });
  }

  private promptFromAction(
    action: FormulaAction | undefined,
    isCustom: boolean,
    current: 'discount' | 'unit_price' | null,
  ): 'discount' | 'unit_price' | null {
    if (isPercentOfUnitPriceAction(action) || quoteImpactFromAction(action) === 'discount_percent') {
      return 'discount';
    }
    if (quoteImpactFromAction(action) === 'unit_price') {
      return 'unit_price';
    }
    if (isCustom) {
      return current;
    }
    return this.valuePromptFor(action?.value);
  }

  private valuePromptFor(id: unknown): 'discount' | 'unit_price' | null {
    const token = String(id || '');
    if (token === 'Item.Discount_Percent') {
      return 'discount';
    }
    if (token === 'Item.Unit_Price') {
      return 'unit_price';
    }
    return null;
  }

  private extractFormattingFromSteps(): void {
    const steps = this.formula.steps || [];
    const formatting = steps.find((s) => s.type === 'formatting');
    this.selectedFormatting = (formatting?.operator as FormattingOperator) || FormattingOperator.NONE;
    this.formula = {
      ...this.formula,
      steps: steps.filter((s) => s.type !== 'formatting'),
    };
  }

  private buildExecutableFormula(): FormulaJSON {
    if (this.formula.logic_mode === 'conditional') {
      return {
        metadata: this.formula.metadata,
        logic_mode: 'conditional',
        condition: { ...this.condition },
        true_action: { ...this.trueAction },
        false_action: { ...this.falseAction },
      };
    }

    const steps = [...(this.formula.steps || [])];
    if (this.selectedFormatting && this.selectedFormatting !== FormattingOperator.NONE) {
      const nextId = steps.length ? Math.max(...steps.map((s) => s.step_id)) + 1 : 1;
      steps.push({
        step_id: nextId,
        type: 'formatting',
        operator: this.selectedFormatting,
      });
    }
    return {
      metadata: this.formula.metadata,
      logic_mode: 'recipe',
      steps,
    };
  }

  private isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  private seedTestValuesFromItem(context?: Record<string, number>): void {
    this.itemSeed = {};
    if (!context || typeof context !== 'object') {
      return;
    }
    for (const [key, raw] of Object.entries(context)) {
      const n = Number(raw);
      if (Number.isFinite(n)) {
        this.itemSeed[key] = n;
      }
    }
    this.applySeededTestValues();
  }

  private applySeededTestValues(): void {
    if (this.itemSeed && Object.keys(this.itemSeed).length) {
      const next = { ...this.testValues };
      let changed = false;
      for (const [key, value] of Object.entries(this.itemSeed)) {
        if (this.userEditedTestKeys.has(key)) {
          continue;
        }
        next[key] = value;
        changed = true;
      }
      if (changed) {
        this.testValues = next;
      }
    }
    this.syncFormulaDiscountToTestBench();
  }

  private singleNumericDiscountAction(): FormulaAction | null {
    const trueDiscount = this.isDiscountAction('true') && typeof this.trueAction.value === 'number';
    const falseDiscount = this.isDiscountAction('false') && typeof this.falseAction.value === 'number';
    if (trueDiscount && !falseDiscount) {
      return this.trueAction;
    }
    if (falseDiscount && !trueDiscount) {
      return this.falseAction;
    }
    if (
      trueDiscount &&
      falseDiscount &&
      this.trueAction.value === this.falseAction.value
    ) {
      return this.trueAction;
    }
    return null;
  }

  private syncFormulaDiscountToTestBench(): void {
    const source = this.singleNumericDiscountAction();
    if (!source || typeof source.value !== 'number') {
      return;
    }
    const percent = clampDiscountPercent(source.value);
    if (this.testValues[DISCOUNT_PERCENT_TOKEN] === percent) {
      return;
    }
    this.testValues = { ...this.testValues, [DISCOUNT_PERCENT_TOKEN]: percent };
  }

  private syncTestBenchDiscountToFormula(percent: number): void {
    const clamped = clampDiscountPercent(percent);
    const trueDiscount = this.isDiscountAction('true') && typeof this.trueAction.value === 'number';
    const falseDiscount = this.isDiscountAction('false') && typeof this.falseAction.value === 'number';
    if (trueDiscount && !falseDiscount) {
      if (this.trueAction.value !== clamped) {
        this.updateAction('true', clamped);
      }
      return;
    }
    if (falseDiscount && !trueDiscount) {
      if (this.falseAction.value !== clamped) {
        this.updateAction('false', clamped);
      }
      return;
    }
    if (trueDiscount && falseDiscount && this.trueAction.value === this.falseAction.value) {
      if (this.trueAction.value !== clamped) {
        this.updateAction('true', clamped);
        this.updateAction('false', clamped);
      }
    }
  }

  private mergeAvailableCustomFields(fields?: any[]): void {
    if (!Array.isArray(fields) || !fields.length) {
      this.includeCurrentFormulaField();
      this.ensureSelectedVariablesVisible();
      return;
    }
    const byId = new Map(this.variables.map((v) => [v.id, v]));
    for (const field of fields) {
      const variable = variableFromCustomField(field);
      if (!variable) {
        continue;
      }
      if (!byId.has(variable.id)) {
        this.variables = [...this.variables, variable];
        byId.set(variable.id, variable);
      }
    }
    this.includeCurrentFormulaField();
    this.ensureSelectedVariablesVisible();
  }

  /** Run order dropdown (value = stored execution order "0".."99", label = position 1, 2, 3…). */
  runOrderOptions: { value: string; label: string }[] = [];

  /** Run order values compare as strings, so a numeric stored value still selects its option. */
  sameRunOrder(a: unknown, b: unknown): boolean {
    return String(a ?? '') === String(b ?? '');
  }

  /** The chosen run order as the position shown to the user (stored 0 = position 1). */
  get runOrderPosition(): number {
    const n = Number(this.headerForm?.get('execution_order')?.value);
    return (Number.isInteger(n) && n >= 0 ? n : 0) + 1;
  }

  /**
   * One position per calculation (the others + this one), never fewer than the stored orders in use,
   * so an existing value is always selectable. Each position names the other calculations there.
   */
  private buildRunOrderOptions(): { value: string; label: string }[] {
    // The calculation being edited is matched by its key; only a brand-new one (no key yet) by name.
    const ownKey = String(this.data?.field_key || '').trim();
    const ownName = String(this.data?.name || '').trim().toLowerCase();
    const isSelf = (f: any) =>
      ownKey ? String(f?.field_key || '').trim() === ownKey : !!ownName && String(f?.name || '').trim().toLowerCase() === ownName;
    const others = (this.data?.availableCustomFields || []).filter(
      (f: FieldTypeRow) => isFormulaType(f?.field_type_id) && !isSelf(f),
    );
    const orderOf = (v: unknown) => {
      const n = Number(v);
      return Number.isInteger(n) && n >= 0 && n <= 99 ? n : 0;
    };
    const namesAt = new Map<number, string[]>();
    for (const f of others) {
      const name = String(f?.name || '').trim();
      if (name) {
        const pos = orderOf(f?.execution_order);
        namesAt.set(pos, [...(namesAt.get(pos) || []), name]);
      }
    }
    const current = orderOf(this.headerForm?.get('execution_order')?.value);
    const last = Math.min(99, Math.max(others.length, current, ...namesAt.keys()));
    const t = (key: string, params?: Record<string, unknown>) => this.translateService.instant(key, params);
    return Array.from({ length: last + 1 }, (_, value) => {
      const n = value + 1;
      const names = namesAt.get(value) || [];
      let label = value === 0 ? t('COMMON.Run order first', { n }) : String(n);
      if (names.length) {
        label += ' · ' + t('COMMON.Run order also', { names: names.join(', ') });
      }
      return { value: String(value), label };
    });
  }

  /** field_key of the formula being edited; for a new one, the slug its name will get. */
  private currentFieldKey(): string {
    const name = String(
      this.headerForm?.get('name')?.value || this.data?.name || this.formula?.metadata?.field_label || '',
    ).trim();
    return String(this.data?.field_key || this.formula?.metadata?.field_key || (name ? slugifyFieldKey(name) : '')).trim();
  }

  /**
   * A formula may not use itself, nor a formula that already uses it (a two-field cycle). The PR
   * ADDED the field being edited to its own pick lists ("QA27 Calc" offered inside QA27 Calc).
   * Dependents are found from the recipes of the fields the item screen passed in.
   */
  private includeCurrentFormulaField(): void {
    const self = variableFromCustomField({
      name: String(this.headerForm?.get('name')?.value || this.data?.name || this.formula?.metadata?.field_label || '').trim(),
      field_key: this.currentFieldKey() || undefined,
      field_type_id: formulaFieldTypeId(),
    });
    if (!self) {
      return;
    }
    const blocked = new Set<string>([self.id]);
    for (const field of this.data?.availableCustomFields || []) {
      if (!isFormulaType(field?.field_type_id)) {
        continue;
      }
      const variable = variableFromCustomField(field);
      if (!variable || variable.id === self.id) {
        continue;
      }
      for (const raw of [field?.item_formula_json, field?.formula_json]) {
        const formula = this.parseFormula(raw);
        if (formula && this.contextService.getUsedVariableIds(formula).includes(self.id)) {
          blocked.add(variable.id);
        }
      }
    }
    if (this.variables.some((v) => blocked.has(v.id))) {
      this.variables = this.variables.filter((v) => !blocked.has(v.id));
    }
  }

  private ensureSelectedVariablesVisible(): void {
    const selected = [
      this.condition.left,
      this.condition.right,
      this.trueAction.value,
      this.falseAction.value,
      ...(this.steps || []).reduce((acc, step) => acc.concat([step.value, step.operand]), [] as unknown[]),
    ];
    const byId = new Map(this.variables.map((v) => [v.id, v]));
    let changed = false;
    for (const value of selected) {
      if (typeof value !== 'string' || (!value.startsWith('Item.') && !value.startsWith('Quote.'))) {
        continue;
      }
      if (byId.has(value)) {
        continue;
      }
      const extra: FormulaVariable = {
        id: value,
        label: this.contextService.labelForVariable(value, this.variables),
        type: 'Number',
      };
      byId.set(value, extra);
      this.variables = [...this.variables, extra];
      changed = true;
    }
    if (changed) {
      this.variables = [...this.variables];
    }
  }

  private buildRecipeAfterConditionalReset(name: string, unit: string): FormulaJSON {
    const existingSteps = (this.formula.steps || []).filter((s) => s.type !== 'formatting');
    const steps = existingSteps.length
      ? existingSteps
      : createDefaultFormula(name || this.defaultName(), unit).steps || [];
    return {
      metadata: this.formula.metadata,
      logic_mode: 'recipe',
      steps,
    };
  }
}
